/**
 * Sector operations — Construction data model and pure validators.
 * R39 B1.a (plan docs/plans/sector-operations.md row B1 :61, bullets :747-749; SO-D16 :317; C§2 pre-start).
 */

import { isValidIsoDate } from '../bookings/pattern.js'
export { isValidIsoDate }

export type ConProjectStatus = 'PLANNING' | 'ACTIVE' | 'ON_HOLD' | 'CLOSED'

export const CON_PROJECT_STATUSES: readonly ConProjectStatus[] = [
  'PLANNING',
  'ACTIVE',
  'ON_HOLD',
  'CLOSED',
] as const

export interface ConPackage {
  key: string // slug, unique in project
  name: string // <= 80
  trade: string // <= 60
  contractorPartyId?: string | null // kind CONTRACTOR
  archived?: boolean
}

export interface ConIndentApprover {
  uid: string
  limit: number | null
}

export interface ConApprovers {
  DPR_REVIEW: string[]
  DPR_APPROVE: string[]
  INDENT_APPROVE: ConIndentApprover[]
  INSPECTION: string[]
  CHANGE_APPROVE: string[]
}

export interface ConProject {
  id: string
  workspaceId: string
  placeId: string // a SITE place
  name: string // <= 120
  code: string // <= 24, uppercase, unique per workspace
  status: ConProjectStatus
  clientPartyId?: string | null
  startDate?: string | null // YYYY-MM-DD
  endDate?: string | null // YYYY-MM-DD, end >= start
  packages: ConPackage[] // <= 60
  approvers: ConApprovers
  createdAt: unknown
  updatedAt: unknown
  createdByUid: string
  updatedByUid: string
}

export type ConDrawingDiscipline = 'ARCH' | 'STRUCT' | 'MEP' | 'CIVIL' | 'LANDSCAPE' | 'OTHER'

export const CON_DRAWING_DISCIPLINES: readonly ConDrawingDiscipline[] = [
  'ARCH',
  'STRUCT',
  'MEP',
  'CIVIL',
  'LANDSCAPE',
  'OTHER',
] as const

export type ConDrawingStatus = 'ACTIVE' | 'WITHDRAWN'

export const CON_DRAWING_STATUSES: readonly ConDrawingStatus[] = [
  'ACTIVE',
  'WITHDRAWN',
] as const

export type ConRevisionStatus = 'CURRENT' | 'SUPERSEDED'

export const CON_REVISION_STATUSES: readonly ConRevisionStatus[] = [
  'CURRENT',
  'SUPERSEDED',
] as const

export interface ConDrawing {
  id: string
  workspaceId: string
  projectId: string
  number: string // <= 40, unique per project, case-insensitive
  title: string // <= 160
  discipline: ConDrawingDiscipline
  packageKey?: string | null
  currentRevision: string // <= 12
  currentRevisionId: string
  status: ConDrawingStatus
  link: string // https URL <= 2000
  createdAt: unknown
  updatedAt: unknown
}

export interface ConDrawingRevision {
  id: string
  drawingId: string
  revision: string // <= 12
  link: string // https URL <= 2000
  note?: string | null // <= 500
  issuedAt: unknown
  issuedByUid: string
  status: ConRevisionStatus
  supersededAt?: unknown | null
  supersededByRevisionId?: string | null
}

export interface ConDrawingRef {
  drawingId: string
  revisionId: string
  revision: string
}

export interface ConDrawingFlag {
  drawingId: string
  oldRevision: string
  newRevision: string
  newRevisionId: string
  flaggedAt: number
  ackRequestId: string
  acknowledgedAt: number | null
  acknowledgedByUid: string | null
}

// ---------------------------------------------------------------------------
// Pure Validators
// ---------------------------------------------------------------------------

const PACKAGE_KEY_REGEX = /^[a-z0-9_-]{1,60}$/

export function isValidPackageKey(key: string): boolean {
  if (typeof key !== 'string') return false
  return PACKAGE_KEY_REGEX.test(key)
}

export function validateConProjectCode(code: string): string {
  if (typeof code !== 'string') {
    throw new Error('Project code must be a string')
  }
  const trimmed = code.trim().toUpperCase()
  if (!trimmed || trimmed.length > 24) {
    throw new Error('Project code must be 1-24 characters')
  }
  return trimmed
}


export function validateProjectDates(
  startDate?: string | null,
  endDate?: string | null,
): { startDate?: string | null; endDate?: string | null } {
  const cleanStart = typeof startDate === 'string' && startDate.trim() ? startDate.trim() : null
  const cleanEnd = typeof endDate === 'string' && endDate.trim() ? endDate.trim() : null

  if (cleanStart && !isValidIsoDate(cleanStart)) {
    throw new Error('Invalid start date (must be YYYY-MM-DD)')
  }
  if (cleanEnd && !isValidIsoDate(cleanEnd)) {
    throw new Error('Invalid end date (must be YYYY-MM-DD)')
  }
  if (cleanStart && cleanEnd && cleanEnd < cleanStart) {
    throw new Error('End date must be on or after start date')
  }
  return { startDate: cleanStart, endDate: cleanEnd }
}

export function isValidHttpsUrl(url: string): boolean {
  if (typeof url !== 'string' || !url.startsWith('https://') || url.length > 2000) {
    return false
  }
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'https:'
  } catch {
    return false
  }
}

export function validateConPackage(raw: unknown): ConPackage {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Package must be an object')
  }
  const p = raw as Record<string, unknown>
  const key = typeof p.key === 'string' ? p.key.trim().toLowerCase() : ''
  if (!isValidPackageKey(key)) {
    throw new Error(`Invalid package key: "${key}" (must be slug 1-60 chars)`)
  }
  const name = typeof p.name === 'string' ? p.name.trim() : ''
  if (!name || name.length > 80) {
    throw new Error(`Invalid package name: must be 1-80 chars`)
  }
  const trade = typeof p.trade === 'string' ? p.trade.trim() : ''
  if (!trade || trade.length > 60) {
    throw new Error(`Invalid package trade: must be 1-60 chars`)
  }
  const contractorPartyId =
    typeof p.contractorPartyId === 'string' && p.contractorPartyId.trim()
      ? p.contractorPartyId.trim()
      : null
  const archived = Boolean(p.archived)

  return {
    key,
    name,
    trade,
    contractorPartyId,
    archived,
  }
}

export function validateConPackages(raw: unknown): ConPackage[] {
  if (!Array.isArray(raw)) {
    throw new Error('Packages must be an array')
  }
  if (raw.length > 60) {
    throw new Error('Packages cannot exceed 60 items')
  }
  const seenKeys = new Set<string>()
  const result: ConPackage[] = []
  for (const item of raw) {
    const pkg = validateConPackage(item)
    if (seenKeys.has(pkg.key)) {
      throw new Error(`Duplicate package key: "${pkg.key}"`)
    }
    seenKeys.add(pkg.key)
    result.push(pkg)
  }
  return result
}

export function validateConApprovers(raw: unknown): ConApprovers {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Approvers must be an object')
  }
  const a = raw as Record<string, unknown>

  const cleanUidList = (val: unknown, roleName: string): string[] => {
    if (!Array.isArray(val)) return []
    if (val.length > 10) {
      throw new Error(`${roleName} approvers cannot exceed 10 uids`)
    }
    const set = new Set<string>()
    for (const u of val) {
      if (typeof u === 'string' && u.trim()) {
        set.add(u.trim())
      }
    }
    return Array.from(set)
  }

  const cleanIndentApprovers = (val: unknown): ConIndentApprover[] => {
    if (!Array.isArray(val)) return []
    if (val.length > 10) {
      throw new Error(`INDENT_APPROVE approvers cannot exceed 10 uids`)
    }
    const seen = new Set<string>()
    const list: ConIndentApprover[] = []
    for (const item of val) {
      if (!item || typeof item !== 'object') continue
      const rec = item as Record<string, unknown>
      const uid = typeof rec.uid === 'string' ? rec.uid.trim() : ''
      if (!uid || seen.has(uid)) continue
      seen.add(uid)
      const limit = typeof rec.limit === 'number' && rec.limit >= 0 ? rec.limit : null
      list.push({ uid, limit })
    }
    return list
  }

  return {
    DPR_REVIEW: cleanUidList(a.DPR_REVIEW, 'DPR_REVIEW'),
    DPR_APPROVE: cleanUidList(a.DPR_APPROVE, 'DPR_APPROVE'),
    INDENT_APPROVE: cleanIndentApprovers(a.INDENT_APPROVE),
    INSPECTION: cleanUidList(a.INSPECTION, 'INSPECTION'),
    CHANGE_APPROVE: cleanUidList(a.CHANGE_APPROVE, 'CHANGE_APPROVE'),
  }
}

export function validateConDrawingNumber(num: string): string {
  if (typeof num !== 'string') {
    throw new Error('Drawing number must be a string')
  }
  const trimmed = num.trim().toUpperCase()
  if (!trimmed || trimmed.length > 40) {
    throw new Error('Drawing number must be 1-40 characters')
  }
  return trimmed
}

export function validateConDrawingRevision(rev: string): string {
  if (typeof rev !== 'string') {
    throw new Error('Drawing revision must be a string')
  }
  const trimmed = rev.trim().toUpperCase()
  if (!trimmed || trimmed.length > 12) {
    throw new Error('Drawing revision must be 1-12 characters')
  }
  return trimmed
}

export function validateConDrawingRef(raw: unknown): ConDrawingRef {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Drawing ref must be an object')
  }
  const r = raw as Record<string, unknown>
  const drawingId = typeof r.drawingId === 'string' ? r.drawingId.trim() : ''
  const revisionId = typeof r.revisionId === 'string' ? r.revisionId.trim() : ''
  const revision = typeof r.revision === 'string' ? r.revision.trim() : ''
  if (!drawingId || !revisionId || !revision) {
    throw new Error('Drawing ref must have drawingId, revisionId, and revision')
  }
  return { drawingId, revisionId, revision }
}
